/*
 * QQQ - Low-code Application Framework for Engineers.
 * Copyright (C) 2021-2022.  Kingsrook, LLC
 * 651 N Broad St Ste 205 # 6917 | Middletown DE 19709 | United States
 * contact@kingsrook.com
 * https://github.com/Kingsrook/
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import {Alert, Button} from "@mui/material";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import ListItemIcon from "@mui/material/ListItemIcon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import {TooltipProps} from "@mui/material/Tooltip/Tooltip";
import {QInstance} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QInstance";
import {QTableMetaData} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QTableMetaData";
import {QRecord} from "@temple-of-syrinx/qqq-frontend-core/lib/model/QRecord";
import QContext from "QContext";
import colors from "qqq/assets/theme/base/colors";
import {QCancelButton, QDeleteButton, QSaveButton} from "qqq/components/buttons/DefaultButtons";
import {UseSavedViewsResult} from "qqq/components/query/useSavedViews";
import RecordQueryView from "qqq/models/query/RecordQueryView";
import {QueryScreenUsage} from "qqq/pages/records/query/RecordQuery";
import FilterUtils from "qqq/utils/qqq/FilterUtils";
import {SavedViewUtils} from "qqq/utils/qqq/SavedViewUtils";
import {compareLabelsForSearch, matchesSearchWords} from "qqq/utils/qqq/SearchWordsMatcher";
import React, {useContext, useRef, useState} from "react";
import {useNavigate} from "react-router-dom";

interface Props
{
   useSavedViewsResult: UseSavedViewsResult;
   metaData: QInstance;
   tableMetaData: QTableMetaData;
   currentSavedView: QRecord;
   tableDefaultView: RecordQueryView;
   view?: RecordQueryView;
   viewAsJson?: string;
   viewOnChangeCallback?: (selectedSavedViewId: number) => void;
   loadingSavedView: boolean;
   queryScreenUsage: QueryScreenUsage;
   copyFilterLinkCallback?: () => void;
   renderQueryHistoryMenuItem?: (closeViewsMenu: () => void) => JSX.Element;
}

function SavedViews({useSavedViewsResult, metaData, tableMetaData, currentSavedView, tableDefaultView, view, viewAsJson, viewOnChangeCallback, loadingSavedView, queryScreenUsage, copyFilterLinkCallback, renderQueryHistoryMenuItem}: Props): JSX.Element
{
   const navigate = useNavigate();

   const yourSavedViews: QRecord[] = useSavedViewsResult?.yourSavedViews.filter((view: QRecord) => view.values.get("type") != "quickView") ?? [];
   const viewsSharedWithYou: QRecord[] = useSavedViewsResult?.viewsSharedWithYou.filter((view: QRecord) => view.values.get("type") != "quickView") ?? [];

   const [savedViewsMenu, setSavedViewsMenu] = useState(null);
   const [savedViewFilterText, setSavedViewFilterText] = useState("");
   const savedViewFilterInputRef = useRef<HTMLInputElement>(null);
   const [isSubmitting, setIsSubmitting] = useState(false);

   const [savePopupOpen, setSavePopupOpen] = useState(false);
   const [isSaveAs, setIsSaveAs] = useState(false);
   const [isRename, setIsRename] = useState(false);
   const [isDelete, setIsDelete] = useState(false);
   const [savedViewNameInputValue, setSavedViewNameInputValue] = useState(null as string);
   const [popupAlertContent, setPopupAlertContent] = useState("");

   const SAVE_OPTION = "Save...";
   const DUPLICATE_OPTION = "Duplicate...";
   const RENAME_OPTION = "Rename...";
   const DELETE_OPTION = "Delete...";
   const CLEAR_OPTION = "New View";
   const NEW_REPORT_OPTION = "Create Report from Current View";

   const {accentColor, accentColorLight, userId: currentUserId} = useContext(QContext);

   /////////////////////////////////////////////////////////////////////////////////////////////
   // this component is used by <RecordQuery> - but that component has different usages -     //
   // e.g., the full-fledged query screen, but also, within other screens (e.g., a modal      //
   // under the FilterAndColumnsSetupWidget).  So, there are some behaviors we only want when //
   // we're on the full-fledged query screen, such as changing the URL with saved view ids.   //
   /////////////////////////////////////////////////////////////////////////////////////////////
   const isQueryScreen = queryScreenUsage == "queryScreen";

   const openSavedViewsMenu = (event: any) => setSavedViewsMenu(event.currentTarget);
   const closeSavedViewsMenu = () =>
   {
      setSavedViewsMenu(null);
      setSavedViewFilterText("");
   };

   const baseView = currentSavedView ? JSON.parse(currentSavedView.values.get("viewJson")) as RecordQueryView : tableDefaultView;
   const viewDiffs = SavedViewUtils.diffViews(tableMetaData, baseView, view);
   let viewIsModified = false;
   if (viewDiffs.length > 0)
   {
      viewIsModified = true;
   }

   /*******************************************************************************
    ** fired when a saved record is clicked from the dropdown
    *******************************************************************************/
   const handleSavedViewRecordOnClick = async (record: QRecord) =>
   {
      setSavePopupOpen(false);
      closeSavedViewsMenu();
      viewOnChangeCallback(record.values.get("id"));
      if (isQueryScreen)
      {
         navigate(`${metaData.getTablePathByName(tableMetaData.name)}/savedView/${record.values.get("id")}`);
      }
   };


   /*******************************************************************************
    ** fired when a save option is selected from the save... button/dropdown combo
    *******************************************************************************/
   const handleDropdownOptionClick = (optionName: string) =>
   {
      setPopupAlertContent("");
      closeSavedViewsMenu();
      setSavePopupOpen(true);
      setIsSaveAs(false);
      setIsRename(false);
      setIsDelete(false);

      switch (optionName)
      {
         case SAVE_OPTION:
            if (currentSavedView == null)
            {
               setSavedViewNameInputValue("");
            }
            break;
         case DUPLICATE_OPTION:
            setSavedViewNameInputValue("");
            setIsSaveAs(true);
            break;
         case CLEAR_OPTION:
            setSavePopupOpen(false);
            viewOnChangeCallback(null);
            if (isQueryScreen)
            {
               navigate(metaData.getTablePathByName(tableMetaData.name));
            }
            break;
         case RENAME_OPTION:
            if (currentSavedView != null)
            {
               setSavedViewNameInputValue(currentSavedView.values.get("label"));
            }
            setIsRename(true);
            break;
         case DELETE_OPTION:
            setIsDelete(true);
            break;
         case NEW_REPORT_OPTION:
            createNewReport();
            break;
      }
   };


   /*******************************************************************************
    **
    *******************************************************************************/
   function createNewReport()
   {
      const defaultValues: { [key: string]: any } = {};
      defaultValues.tableName = tableMetaData.name;

      let filterForBackend = JSON.parse(JSON.stringify(view.queryFilter));
      filterForBackend = FilterUtils.prepQueryFilterForBackend(tableMetaData, filterForBackend);

      defaultValues.queryFilterJson = JSON.stringify(filterForBackend);
      defaultValues.columnsJson = JSON.stringify(view.queryColumns);
      navigate(`${metaData.getTablePathByName("savedReport")}/create#defaultValues=${encodeURIComponent(JSON.stringify(defaultValues))}`);
   }


   /*******************************************************************************
    ** fired when save or delete button saved on confirmation dialogs
    *******************************************************************************/
   async function handleDialogButtonOnClick()
   {
      try
      {
         setPopupAlertContent("");
         setIsSubmitting(true);

         const formData = new FormData();
         if (isDelete)
         {
            formData.append("id", currentSavedView.values.get("id"));
            await useSavedViewsResult.makeSavedViewRequest("deleteSavedView", formData);

            setSavePopupOpen(false);

            await (async () =>
            {
               handleDropdownOptionClick(CLEAR_OPTION);
            })();
         }
         else
         {
            formData.append("tableName", tableMetaData.name);

            /////////////////////////////////////////////////////////////////////////////////////////////////
            // clone view via json serialization/deserialization                                           //
            // then replace the viewJson in it with a copy that has had its possible values changed to ids //
            // then stringify that for the backend                                                         //
            /////////////////////////////////////////////////////////////////////////////////////////////////
            const viewObject = JSON.parse(JSON.stringify(view));
            viewObject.queryFilter = JSON.parse(JSON.stringify(FilterUtils.convertFilterPossibleValuesToIds(viewObject.queryFilter)));
            viewObject.queryFilter = FilterUtils.prepQueryFilterForBackend(tableMetaData, viewObject.queryFilter);

            ////////////////////////////////////////////////////////////////////////////
            // strip away incomplete filters too, just for cleaner saved view filters //
            ////////////////////////////////////////////////////////////////////////////
            FilterUtils.stripAwayIncompleteCriteria(viewObject.queryFilter);

            formData.append("viewJson", JSON.stringify(viewObject));

            if (isSaveAs || isRename || currentSavedView == null)
            {
               formData.append("label", savedViewNameInputValue);
               if (currentSavedView != null && isRename)
               {
                  formData.append("id", currentSavedView.values.get("id"));
               }
            }
            else
            {
               formData.append("id", currentSavedView.values.get("id"));
               formData.append("label", currentSavedView?.values.get("label"));
            }
            const recordList = await useSavedViewsResult.makeSavedViewRequest("storeSavedView", formData);
            await (async () =>
            {
               if (recordList && recordList.length > 0)
               {
                  useSavedViewsResult.loadSavedViews();
                  handleSavedViewRecordOnClick(recordList[0]);
               }
            })();
         }

         setSavePopupOpen(false);
      }
      catch (e: any)
      {
         let message = JSON.stringify(e);
         if (typeof e == "string")
         {
            message = e;
         }
         else if (typeof e == "object" && e.message)
         {
            message = e.message;
         }

         setPopupAlertContent(message);
         console.log(`Setting error: ${message}`);
      }
      finally
      {
         setIsSubmitting(false);
      }
   }


   /*******************************************************************************
    ** stores the current dialog input text to state
    *******************************************************************************/
   const handleSaveDialogInputChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) =>
   {
      setSavedViewNameInputValue(event.target.value);
   };


   /*******************************************************************************
    ** closes current dialog
    *******************************************************************************/
   const handleSavePopupClose = () =>
   {
      setSavePopupOpen(false);
   };

   const hasStorePermission = metaData?.processes.has("storeSavedView");
   const hasDeletePermission = metaData?.processes.has("deleteSavedView");
   const hasQueryPermission = metaData?.processes.has("querySavedView");
   const hasSavedReportsPermission = metaData?.tables.has("savedReport");

   const tooltipMaxWidth = (maxWidth: string) =>
   {
      return ({
         slotProps: {
            tooltip: {
               sx: {
                  maxWidth: maxWidth
               }
            }
         }
      });
   };

   const menuTooltipAttribs = {...tooltipMaxWidth("250px"), placement: "left", enterDelay: 1000} as TooltipProps;

   let disabledBecauseNotOwner = false;
   let notOwnerTooltipText = null;
   if (currentSavedView && currentSavedView.values.get("userId") != currentUserId)
   {
      disabledBecauseNotOwner = true;
      notOwnerTooltipText = "You may not save changes to this view, because you are not its owner.";
   }

   const trimmedFilterText = savedViewFilterText.trim().replace(/\s+/g, " ");
   const savedViewMatchesFilter = (record: QRecord): boolean => trimmedFilterText == "" || matchesSearchWords(`${record.values.get("label") ?? ""}`, trimmedFilterText);
   const filterAndSortSavedViews = (records: QRecord[]): QRecord[] =>
   {
      const matches = records.filter(savedViewMatchesFilter);
      if (trimmedFilterText != "")
      {
         matches.sort((a, b) => compareLabelsForSearch(`${a.values.get("label") ?? ""}`, `${b.values.get("label") ?? ""}`, trimmedFilterText));
      }
      return (matches);
   };
   const filteredYourSavedViews = filterAndSortSavedViews(yourSavedViews);
   const filteredViewsSharedWithYou = filterAndSortSavedViews(viewsSharedWithYou);
   const haveAnySavedViews = yourSavedViews.length + viewsSharedWithYou.length > 0;

   //////////////////////////////////////////////////////////////////////////////////////
   // keys typed in the filter input belong to the input - without this, the menu's    //
   // own key handling would treat letters as type-ahead and move focus to a matching  //
   // item.  The exceptions: Escape and Tab still go to the menu (to close it, as      //
   // elsewhere), ArrowDown moves from the input to the first matching saved view, and //
   // Enter opens that first match.                                                    //
   //////////////////////////////////////////////////////////////////////////////////////
   /////////////////////////////////////////////////////////////////////////////////////
   // opening the menu puts the cursor in the filter input, so a user can open it and //
   // start typing a view's name right away.  (The menu's own auto-focus is off when  //
   // the input is there, so it can't take focus back from the input.)                //
   /////////////////////////////////////////////////////////////////////////////////////
   const focusSavedViewFilterInput = () =>
   {
      /////////////////////////////////////////////////////////////////////////////
      // this runs at the start of the open animation and again at its end - if  //
      // the user has already arrowed onto a menu item by then, leave them there //
      /////////////////////////////////////////////////////////////////////////////
      if ((document.activeElement as HTMLElement)?.closest?.("li[role='menuitem']"))
      {
         return;
      }
      savedViewFilterInputRef.current?.focus();
   };

   const handleSavedViewFilterKeyDown = (event: React.KeyboardEvent<HTMLElement>) =>
   {
      if (event.key == "Escape" || event.key == "Tab")
      {
         return;
      }

      event.stopPropagation();

      if (event.key == "ArrowDown")
      {
         event.preventDefault();
         const firstMatchingItem = event.currentTarget.closest("ul")?.querySelector<HTMLElement>("li.savedViewMenuItem");
         firstMatchingItem?.focus();
      }
      else if (event.key == "ArrowUp")
      {
         ///////////////////////////////////////////////////////////////////////////////
         // up to the nearest enabled item above the input - the last of the actions, //
         // from where the menu's own arrow keys take over                            //
         ///////////////////////////////////////////////////////////////////////////////
         event.preventDefault();
         let candidate = event.currentTarget.closest(".savedViewFilterBox")?.previousElementSibling;
         while (candidate && !(candidate instanceof HTMLElement && candidate.hasAttribute("tabindex") && candidate.getAttribute("aria-disabled") != "true"))
         {
            candidate = candidate.previousElementSibling;
         }
         (candidate as HTMLElement)?.focus();
      }
      else if (event.key == "Enter" && trimmedFilterText != "")
      {
         event.preventDefault();
         const firstMatch = filteredYourSavedViews[0] ?? filteredViewsSharedWithYou[0];
         if (firstMatch)
         {
            handleSavedViewRecordOnClick(firstMatch);
         }
      }
   };

   //////////////////////////////////////////////////////////////////////////////////////////
   // the actions that work from the current view (rather than saving or resetting it)     //
   // sit in their own group - with a divider above them only when at least one of them    //
   // is present, so a menu missing all of them doesn't end up with two dividers in a row. //
   //////////////////////////////////////////////////////////////////////////////////////////
   const haveCurrentViewActions = isQueryScreen && (hasSavedReportsPermission || copyFilterLinkCallback != null || renderQueryHistoryMenuItem != null);

   ///////////////////////////////////////////////////////////////////////////////////////
   // a View Actions item with its tooltip.  The menu's arrow keys only move between    //
   // its own children that can take focus, so an enabled item is the Tooltip's direct  //
   // child (no wrapper) - else arrow keys skip it, and the first item's wrapper took   //
   // focus with the browser's outline instead of the item's own highlight.  A disabled //
   // MenuItem gets no mouse events, so its tooltip needs a span to hang on; arrow keys //
   // skip disabled items anyway.                                                       //
   ///////////////////////////////////////////////////////////////////////////////////////
   const withMenuTooltip = (title: React.ReactNode, isDisabled: boolean, menuItem: JSX.Element): JSX.Element =>
   {
      if (isDisabled)
      {
         ////////////////////////////////////////////////////////////////////////////////
         // the menu list makes its first non-disabled child focusable - and this span //
         // isn't marked disabled, so it would get tabIndex 0, take arrow-key focus,   //
         // and show the browser's outline.  tabIndex -1 here wins over the one the    //
         // list passes through the Tooltip, and aria-disabled makes the list skip it. //
         ////////////////////////////////////////////////////////////////////////////////
         return (<Tooltip {...menuTooltipAttribs} title={title}><span tabIndex={-1} aria-disabled="true">{menuItem}</span></Tooltip>);
      }
      return (<Tooltip {...menuTooltipAttribs} title={title}>{menuItem}</Tooltip>);
   };

   const renderSavedViewsMenu = tableMetaData && (
      <Menu
         anchorEl={savedViewsMenu}
         anchorOrigin={{vertical: "bottom", horizontal: "left",}}
         transformOrigin={{vertical: "top", horizontal: "left",}}
         open={Boolean(savedViewsMenu)}
         onClose={closeSavedViewsMenu}
         keepMounted
         autoFocus={!haveAnySavedViews}
         TransitionProps={{onEntering: focusSavedViewFilterInput, onEntered: focusSavedViewFilterInput}}
         PaperProps={{style: {maxHeight: "calc(100vh - 200px)", minWidth: "300px"}}}
      >
         {
            isQueryScreen &&
            <MenuItem sx={{width: "300px"}} disabled style={{"opacity": "initial"}}><b>View Actions</b></MenuItem>
         }
         {
            isQueryScreen && hasStorePermission &&
            withMenuTooltip(notOwnerTooltipText ?? <>Save your current filters, columns and settings, for quick re-use at a later time.<br /><br />You will be prompted to enter a name if you choose this option.</>, disabledBecauseNotOwner,
               <MenuItem disabled={disabledBecauseNotOwner} onClick={() => handleDropdownOptionClick(SAVE_OPTION)}>
                  <ListItemIcon><Icon>save</Icon></ListItemIcon>
                  {currentSavedView ? "Save..." : "Save As..."}
               </MenuItem>)
         }
         {
            isQueryScreen && hasStorePermission && currentSavedView != null &&
            withMenuTooltip(notOwnerTooltipText ?? "Change the name for this saved view.", disabledBecauseNotOwner,
               <MenuItem disabled={disabledBecauseNotOwner} onClick={() => handleDropdownOptionClick(RENAME_OPTION)}>
                  <ListItemIcon><Icon>edit</Icon></ListItemIcon>
                  Rename...
               </MenuItem>)
         }
         {
            isQueryScreen && hasStorePermission && currentSavedView != null &&
            withMenuTooltip("Save a new copy this view, with a different name, separate from the original.", false,
               <MenuItem onClick={() => handleDropdownOptionClick(DUPLICATE_OPTION)}>
                  <ListItemIcon><Icon>content_copy</Icon></ListItemIcon>
                  Save As...
               </MenuItem>)
         }
         {
            isQueryScreen && hasDeletePermission && currentSavedView != null &&
            withMenuTooltip(notOwnerTooltipText ?? "Delete this saved view.", disabledBecauseNotOwner,
               <MenuItem disabled={disabledBecauseNotOwner} onClick={() => handleDropdownOptionClick(DELETE_OPTION)}>
                  <ListItemIcon><Icon>delete</Icon></ListItemIcon>
                  Delete...
               </MenuItem>)
         }
         {
            isQueryScreen &&
            withMenuTooltip("Create a new view of this table, resetting the filters and columns to their defaults.", false,
               <MenuItem onClick={() => handleDropdownOptionClick(CLEAR_OPTION)}>
                  <ListItemIcon><Icon>monitor</Icon></ListItemIcon>
                  New View
               </MenuItem>)
         }
         {
            haveCurrentViewActions && <Divider className="currentViewActionsDivider" />
         }
         {
            isQueryScreen && hasSavedReportsPermission &&
            withMenuTooltip("Create a new Saved Report using your current view of this table as a starting point.", false,
               <MenuItem onClick={() => handleDropdownOptionClick(NEW_REPORT_OPTION)}>
                  <ListItemIcon><Icon>article</Icon></ListItemIcon>
                  Create Report from Current View
               </MenuItem>)
         }
         {
            isQueryScreen && copyFilterLinkCallback &&
            withMenuTooltip("Copy a link to this table with your current filter, sort and columns, for sharing.", false,
               <MenuItem id="copyFilterLinkMenuItem" onClick={() =>
               {
                  closeSavedViewsMenu();
                  copyFilterLinkCallback();
               }}>
                  <ListItemIcon><Icon>link</Icon></ListItemIcon>
                  Copy Link to Current View
               </MenuItem>)
         }
         {
            isQueryScreen && renderQueryHistoryMenuItem && renderQueryHistoryMenuItem(closeSavedViewsMenu)
         }
         {
            isQueryScreen && <Divider />
         }
         {
            haveAnySavedViews &&
            <Box className="savedViewFilterBox" px="1rem" py="0.5rem">
               <TextField
                  id="savedViewFilterInput"
                  inputRef={savedViewFilterInputRef}
                  variant="outlined"
                  sx={{"& .MuiOutlinedInput-input": {py: "0.5rem"}}}
                  placeholder="Filter saved views"
                  autoComplete="off"
                  fullWidth
                  value={savedViewFilterText}
                  onChange={(event) => setSavedViewFilterText(event.target.value)}
                  onKeyDown={handleSavedViewFilterKeyDown}
                  InputProps={{startAdornment: <Icon sx={{mr: "-2px", color: colors.gray.main}}>search</Icon>}}
               />
            </Box>
         }
         <MenuItem disabled style={{"opacity": "initial"}}><b>Your Saved Views</b></MenuItem>
         {
            filteredYourSavedViews.length > 0 ? (
               filteredYourSavedViews.map((record: QRecord) =>
                  <MenuItem className="savedViewMenuItem" sx={{paddingLeft: "50px"}} key={`savedFiler-${record.values.get("id")}`} onClick={() => handleSavedViewRecordOnClick(record)}>
                     {record.values.get("label")}
                  </MenuItem>
               )
            ) : (
               <MenuItem disabled sx={{opacity: "1 !important"}}>
                  <i>{yourSavedViews.length > 0 ? "None of your saved views match the filter." : "You do not have any saved views for this table."}</i>
               </MenuItem>
            )
         }
         <MenuItem disabled style={{"opacity": "initial"}}><b>Views Shared with you</b></MenuItem>
         {
            filteredViewsSharedWithYou.length > 0 ? (
               filteredViewsSharedWithYou.map((record: QRecord) =>
                  <MenuItem className="savedViewMenuItem" sx={{paddingLeft: "50px"}} key={`savedFiler-${record.values.get("id")}`} onClick={() => handleSavedViewRecordOnClick(record)}>
                     {record.values.get("label")}
                  </MenuItem>
               )
            ) : (
               <MenuItem disabled sx={{opacity: "1 !important"}}>
                  <i>{viewsSharedWithYou.length > 0 ? "None of the views shared with you match the filter." : "You do not have any views shared with you for this table."}</i>
               </MenuItem>
            )
         }
      </Menu>
   );

   let buttonText = "Views";
   let buttonBackground = "unset";
   let buttonBorder = colors.grayLines.main + " !important";
   let buttonColor = colors.gray.main;
   let buttonVariant: "outlined" | "contained" | "text" = "outlined";
   let buttonColorName: "secondary" | "primary" = "secondary";
   let buttonState: string = "empty";

   const savedViewId = currentSavedView?.values.get("id");
   if (currentSavedView && ([...yourSavedViews, ...viewsSharedWithYou]).map(view => view.values.get("id")).includes(savedViewId))
   {
      if (viewIsModified)
      {
         buttonBackground = accentColorLight + " !important";
         buttonBorder = buttonBackground + " !important";
         buttonColor = accentColor + " !important";
         buttonVariant = "contained";
         buttonColorName = "primary";
         buttonState = "dirty";
      }
      else
      {
         buttonBackground = accentColor + " !important";
         buttonBorder = buttonBackground + " !important";
         buttonColor = "#FFFFFF" + " !important";
         buttonVariant = "contained";
         buttonColorName = "primary";
         buttonState = "clean";
      }
   }

   const buttonStyles = {
      border: `1px solid ${buttonBorder}`,
      backgroundColor: buttonBackground,
      color: buttonColor,
      boxShadow: "none",
      "&:focus:not(:hover)": {
         color: buttonColor,
         backgroundColor: buttonBackground,
      },
      "&:hover": {
         color: buttonColor,
         backgroundColor: buttonBackground,
         boxShadow: "none",
         filter: "unset"
      }
   };

   /*******************************************************************************
    **
    *******************************************************************************/
   function isSaveButtonDisabled(): boolean
   {
      if (isSubmitting)
      {
         return (true);
      }

      const haveInputText = (savedViewNameInputValue != null && savedViewNameInputValue.trim() != "");

      if (isSaveAs || isRename || currentSavedView == null)
      {
         if (!haveInputText)
         {
            return (true);
         }
      }

      return (false);
   }

   const linkButtonStyle = {
      minWidth: "unset",
      textTransform: "none",
      fontSize: "0.875rem",
      fontWeight: "500",
      padding: "0.5rem"
   };

   return (
      hasQueryPermission && tableMetaData ? (
         <>
            <Box order="1" mr={"0.5rem"}>
               <Button
                  variant={buttonVariant}
                  color={buttonColorName}
                  data-button-state={buttonState}
                  onClick={openSavedViewsMenu}
                  data-qqq-id="button-views"
                  sx={{
                     borderRadius: "0.75rem",
                     textTransform: "none",
                     fontWeight: 500,
                     fontSize: "0.875rem",
                     p: "0.5rem",
                     ...buttonStyles
                  }}
               >
                  <Icon sx={{mr: "0.5rem"}}>save</Icon>
                  {buttonText}
                  <Icon sx={{ml: "0.5rem"}}>keyboard_arrow_down</Icon>
               </Button>
               {renderSavedViewsMenu}
            </Box>
            <Box order="3" display="flex" justifyContent="center" flexDirection="column">
               <Box pl={2} pr={2} sx={{display: "flex", alignItems: "center"}}>
                  {
                     !currentSavedView && viewIsModified && <>
                        {
                           isQueryScreen && <>
                              <Tooltip {...tooltipMaxWidth("24rem")} sx={{cursor: "pointer"}} title={<>
                                 <b>Unsaved Changes</b>
                                 <ul style={{padding: "0.5rem 1rem"}}>
                                    {
                                       viewDiffs.map((s: string, i: number) => <li key={i}>{s}</li>)
                                    }
                                 </ul>
                              </>}>
                                 <Button disableRipple={true} sx={linkButtonStyle} onClick={() => handleDropdownOptionClick(SAVE_OPTION)}>Save View As&hellip;</Button>
                              </Tooltip>

                              {/* vertical rule */}
                              <Box display="inline-block" borderLeft={`1px solid ${colors.grayLines.main}`} height="1rem" width="1px" position="relative" />
                           </>
                        }

                        <Button disableRipple={true} sx={{color: colors.gray.main, ...linkButtonStyle}} onClick={() => handleDropdownOptionClick(CLEAR_OPTION)}>Reset All Changes</Button>
                     </>
                  }
                  {
                     isQueryScreen && currentSavedView && viewIsModified && <>
                        <Tooltip {...tooltipMaxWidth("24rem")} sx={{cursor: "pointer"}} title={<>
                           <b>Unsaved Changes</b>
                           <ul style={{padding: "0.5rem 1rem"}}>
                              {
                                 viewDiffs.map((s: string, i: number) => <li key={i}>{s}</li>)
                              }
                           </ul>
                           {
                              notOwnerTooltipText && <i>{notOwnerTooltipText}</i>
                           }
                        </>}>
                           <Box display="inline" sx={{...linkButtonStyle, p: 0, cursor: "default", position: "relative", top: "-1px"}}>{viewDiffs.length} Unsaved Change{viewDiffs.length == 1 ? "" : "s"}</Box>
                        </Tooltip>

                        {disabledBecauseNotOwner ? <>&nbsp;&nbsp;</> : <Button disableRipple={true} sx={linkButtonStyle} onClick={() => handleDropdownOptionClick(SAVE_OPTION)}>Save&hellip;</Button>}

                        {/* vertical rule */}
                        <Box display="inline-block" borderLeft={`1px solid ${colors.grayLines.main}`} height="1rem" width="1px" position="relative" />

                        <Button disableRipple={true} sx={{color: colors.gray.main, ...linkButtonStyle}} onClick={() => handleSavedViewRecordOnClick(currentSavedView)}>Reset All Changes</Button>
                     </>
                  }
                  {
                     !isQueryScreen && currentSavedView &&
                     <Box>
                        <Box display="inline-block" fontSize="0.875rem" fontWeight="500" sx={{position: "relative", top: "-1px"}}>
                           {currentSavedView.values.get("label")}
                        </Box>

                        {
                           viewIsModified &&
                           <>
                              <Tooltip {...tooltipMaxWidth("24rem")} sx={{cursor: "pointer"}} title={<>
                                 <b>Changes</b>
                                 <ul style={{padding: "0.5rem 1rem"}}>
                                    {
                                       viewDiffs.map((s: string, i: number) => <li key={i}>{s}</li>)
                                    }
                                 </ul>
                              </>}>
                                 <Box display="inline" ml="0.25rem" mr="0.25rem" sx={{...linkButtonStyle, p: 0, cursor: "default", position: "relative", top: "-1px"}}>with {viewDiffs.length} Change{viewDiffs.length == 1 ? "" : "s"}</Box>
                              </Tooltip>
                              <Button disableRipple={true} sx={{color: colors.gray.main, ...linkButtonStyle}} onClick={() => handleSavedViewRecordOnClick(currentSavedView)}>Reset Changes</Button>
                           </>
                        }

                        {/* vertical rule */}
                        <Box display="inline-block" ml="0.25rem" borderLeft={`1px solid ${colors.grayLines.main}`} height="1rem" width="1px" position="relative" />
                        <Button disableRipple={true} sx={{color: colors.gray.main, ...linkButtonStyle}} onClick={() => handleDropdownOptionClick(CLEAR_OPTION)}>Reset to New View</Button>
                     </Box>
                  }
               </Box>
            </Box>
            {
               <Dialog
                  open={savePopupOpen}
                  onClose={handleSavePopupClose}
                  aria-labelledby="alert-dialog-title"
                  aria-describedby="alert-dialog-description"
                  onKeyPress={(e) =>
                  {
                     ////////////////////////////////////////////////////
                     // make user actually hit delete button           //
                     // but for other modes, let Enter submit the form //
                     ////////////////////////////////////////////////////
                     if (e.key == "Enter" && !isDelete)
                     {
                        handleDialogButtonOnClick();
                     }
                  }}
               >
                  {
                     currentSavedView ? (
                        isDelete ? (
                           <DialogTitle id="alert-dialog-title">Delete View</DialogTitle>
                        ) : (
                           isSaveAs ? (
                              <DialogTitle id="alert-dialog-title">Save View As</DialogTitle>
                           ) : (
                              isRename ? (
                                 <DialogTitle id="alert-dialog-title">Rename View</DialogTitle>
                              ) : (
                                 <DialogTitle id="alert-dialog-title">Update Existing View</DialogTitle>
                              )
                           )
                        )
                     ) : (
                        <DialogTitle id="alert-dialog-title">Save New View</DialogTitle>
                     )
                  }
                  <DialogContent sx={{width: "500px"}}>
                     {popupAlertContent ? (
                        <Box mb={1}>
                           <Alert severity="error" onClose={() => setPopupAlertContent("")}>{popupAlertContent}</Alert>
                        </Box>
                     ) : ("")}
                     {
                        (!currentSavedView || isSaveAs || isRename) && !isDelete ? (
                           <Box>
                              {
                                 isSaveAs ? (
                                    <Box mb={3}>Enter a name for this new saved view.</Box>
                                 ) : (
                                    <Box mb={3}>Enter a new name for this saved view.</Box>
                                 )
                              }
                              <TextField
                                 autoFocus
                                 name="custom-delimiter-value"
                                 placeholder="View Name"
                                 inputProps={{width: "100%", maxLength: 100}}
                                 value={savedViewNameInputValue}
                                 sx={{width: "100%"}}
                                 onChange={handleSaveDialogInputChange}
                                 onFocus={event =>
                                 {
                                    event.target.select();
                                 }}
                              />
                           </Box>
                        ) : (
                           isDelete ? (
                              <Box>Are you sure you want to delete the view {`'${currentSavedView?.values.get("label")}'`}?</Box>
                           ) : (
                              <Box>Are you sure you want to update the view {`'${currentSavedView?.values.get("label")}'`}?</Box>
                           )
                        )
                     }
                  </DialogContent>
                  <DialogActions>
                     <QCancelButton onClickHandler={handleSavePopupClose} disabled={false} />
                     {
                        isDelete ?
                           <QDeleteButton onClickHandler={handleDialogButtonOnClick} disabled={isSubmitting} />
                           :
                           <QSaveButton label="Save" onClickHandler={handleDialogButtonOnClick} disabled={isSaveButtonDisabled()} />
                     }
                  </DialogActions>
               </Dialog>
            }
         </>
      ) : null
   );
}

export default SavedViews;
